import { Box, Paper } from '@mui/material';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { DAY_COUNT } from '../../../pages/EmployeesSchedule/employeesScheduleStyles';
import { useGetEmployeeTimeOffAvailability } from '../../../services/react-query/employee_time_offs/useGetEmployeeTimeOffAvailability';
import { AvailabilityStatus, EmployeeTimeOffAvailabilityRecord, EmployeeTimeOffMode } from '../../../types/EmployeeTimeOff';
import { Loading } from '../../../uikit';
import { getAvailabilityStatusFromRecords } from '../../../utility/availability';
import { humanizeDateRange } from '../../../utility/dateUtils';
import TimeOffModal from '../../common/employees/TimeOffModal';
import { paperContainerStyles, tilesContainerStyles } from '../dashboardStyles';
import { AvailabilityFooter } from './AvailabilityFooter';
import { loadingContainerStyles } from './availabilityPanelStyles';
import { AvailabilityTile } from './AvailabilityTile';
import { AvailabilityHeader } from './AvailbilityHeader';
import ScheduleInfoModal from './ScheduleInfoModal';
import { FEATURE_FLAGS, useGetFeatureFlags } from '../../../services/react-query/users/useGetFeatureFlags';

interface AvailabilityPanelProps {
  currentEmployeeUuid?: string;
}

type SelectedTimeOffRecord = {
  uuid?: string;
  selectedDate?: string;
};
export function AvailabilityPanel({ currentEmployeeUuid }: AvailabilityPanelProps) {
  const [weekStart, setWeekStart] = useState(DateTime.local().startOf('week'));
  const [selectedTimeOffRecord, setSelectedTimeOffRecord] = useState<SelectedTimeOffRecord | null>(null);
  const [scheduleInfoOpen, setScheduleInfoOpen] = useState(false);
  const { data: featureFlags } = useGetFeatureFlags();
  const isFeatureEnabled = featureFlags?.[FEATURE_FLAGS.employeeAvailability];

  const isModalOpen = !!selectedTimeOffRecord;

  const weekEnd = useMemo(() => weekStart.plus({ days: DAY_COUNT }), [weekStart]);

  const weekDays = useMemo(
    () =>
      _.times(DAY_COUNT, (index) => {
        const date = weekStart.plus({ days: index });
        return {
          date,
          key: date.toISODate(),
        };
      }),
    [weekStart]
  );

  const { data: employeeTimeOffAvailabilityData, isLoading } = useGetEmployeeTimeOffAvailability({
    enabled: !!currentEmployeeUuid && isFeatureEnabled,
    params: {
      employee_uuids: [_.toString(currentEmployeeUuid)],
      start_date: weekStart.toISODate(),
      end_date: weekEnd.toISODate(),
    },
  });

  if (!isFeatureEnabled) {
    return null;
  }

  if (!currentEmployeeUuid) {
    return <Loading />;
  }

  const employeeAvailability = employeeTimeOffAvailabilityData?.[currentEmployeeUuid] || {};

  const handleCloseModal = () => {
    setSelectedTimeOffRecord(null);
  };

  const handleDayClick = (dateKey: string, records: EmployeeTimeOffAvailabilityRecord[]) => {
    const record = _.find(records, { mode: EmployeeTimeOffMode.FULL }) ?? _.find(records, { mode: EmployeeTimeOffMode.WEEKLY });

    if (record?.mode === EmployeeTimeOffMode.WEEKLY) {
      setScheduleInfoOpen(true);
      return;
    }

    setSelectedTimeOffRecord({
      uuid: record?.uuid || 'new',
      selectedDate: dateKey,
    });
  };

  const counts = weekDays.reduce(
    (acc, day) => {
      const records = employeeAvailability[day.key] || [];

      switch (getAvailabilityStatusFromRecords(records)) {
        case AvailabilityStatus.AVAILABLE:
          acc.available += 1;
          break;
        case AvailabilityStatus.REQUESTED_OFF:
          acc.requestedOff += 1;
          break;
        case AvailabilityStatus.UNAVAILABLE:
          acc.unavailable += 1;
          break;
      }

      return acc;
    },
    {
      available: 0,
      requestedOff: 0,
      unavailable: 0,
    }
  );

  const weekEndDisplayDate = weekStart.plus({ days: 6 });
  const weekLabel = `${humanizeDateRange(weekStart.toISODate(), weekEndDisplayDate.toISODate())}`;

  return (
    <Box component={Paper} sx={paperContainerStyles}>
      <AvailabilityHeader
        label={weekLabel}
        onPrevious={() => setWeekStart(weekStart.minus({ days: DAY_COUNT }))}
        onNext={() => setWeekStart(weekStart.plus({ days: DAY_COUNT }))}
      />

      {isLoading ? (
        <Box sx={loadingContainerStyles}>
          <Loading />
        </Box>
      ) : (
        <>
          <Box sx={tilesContainerStyles}>
            {_.map(weekDays, (day) => (
              <AvailabilityTile key={day.key} date={day.date} records={employeeAvailability[day.key] || []} onClick={handleDayClick} />
            ))}
          </Box>

          <AvailabilityFooter counts={counts} />
        </>
      )}

      {isModalOpen && (
        <TimeOffModal
          open={isModalOpen}
          uuid={selectedTimeOffRecord?.uuid}
          employeeUuid={currentEmployeeUuid}
          selectedDate={selectedTimeOffRecord?.selectedDate}
          mode={EmployeeTimeOffMode.FULL}
          onClose={handleCloseModal}
        />
      )}

      <ScheduleInfoModal open={scheduleInfoOpen} onClose={() => setScheduleInfoOpen(false)} />
    </Box>
  );
}
